import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import Input from './Input';
import Select from './Select';
import Button from './Buttons';
import InviteLinkBox from './InviteLinkBox';
import { useApi } from '../hooks/useApi';
import { fetchInviteRoleOptions } from '../services/userRolesService';

/**
 * Standalone modal for inviting a new user via email.
 * Used both in UserManagement and inline from any UserSelect "+" button.
 *
 * Props:
 *   isOpen    – boolean
 *   onClose   – () => void
 *   onSuccess – (inviteData?) => void  (optional, called after a successful invite with
 *               { userInviteId, email, fullName, emailDelivery }; when email delivery is disabled it
 *               fires when the user closes the dialog, after they have seen the invite link)
 */
const InviteUserModal = ({ isOpen, onClose, onSuccess, assignableOnly = false }) => {
    const apiCall = useApi();
    const [formData, setFormData] = useState({ email: '', fullName: '', role: '', scope: '' });
    const [roleOptions, setRoleOptions] = useState([]);
    const [sending, setSending] = useState(false);
    const [loadingRoles, setLoadingRoles] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    // Set when the backend reports emailDelivery "disabled": { message, inviteUrl, payload }
    const [disabledResult, setDisabledResult] = useState(null);

    useEffect(() => {
        if (!isOpen) return;
        setDisabledResult(null);
        setFormData({ email: '', fullName: '', role: '', scope: '' });
        setSuccessMessage('');
        setErrorMessage('');
        setLoadingRoles(true);
        fetchInviteRoleOptions(apiCall)
            .then(opts => {
                let filtered = opts || [];
                if (assignableOnly) {
                    filtered = filtered.filter(r => r.role === 'CONTRIBUTOR');
                    setFormData(prev => ({ ...prev, role: 'CONTRIBUTOR', scope: 'SELF' }));
                }
                setRoleOptions(filtered);
            })
            .catch(() => {})
            .finally(() => setLoadingRoles(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleRoleChange = (e) => {
        const compositeValue = e.target.value;
        if (!compositeValue) {
            setFormData({ ...formData, role: '', scope: '' });
            return;
        }
        const lastUnderscore = compositeValue.lastIndexOf('_');
        const role = compositeValue.substring(0, lastUnderscore);
        const scope = compositeValue.substring(lastUnderscore + 1);
        setFormData({ ...formData, role, scope });
    };

    const checkDomain = async (email) => {
        const domain = email.includes('@') ? email.split('@').pop() : '';
        if (!domain) return;
        try {
            const result = await apiCall('GET', `/signup/check-domain?domain=${encodeURIComponent(domain)}`);
            if (result?.isPublic) {
                setErrorMessage('Public email domains are not allowed. Please use a business email address.');
            } else if (result?.isPublic === false) {
                setErrorMessage('');
            }
            // if result is undefined/null (unexpected response), leave existing message intact
        } catch {
            // network failure — leave existing error message intact; backend enforces on submit
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setSending(true);
        setSuccessMessage('');
        setErrorMessage('');
        try {
            const response = await apiCall('POST', '/userInvite/invite', {
                email: formData.email,
                fullName: formData.fullName,
                role: formData.role,
                scope: formData.scope,
            });
            const backendMessage = response?.message || '';
            if (response?.emailDelivery === 'disabled') {
                // No email goes out on this installation: show the link (when the backend returned one)
                // and keep the dialog open. onSuccess fires from Close so callers that close the dialog
                // on success cannot drop the link. The link stays in component state only.
                setDisabledResult({
                    message: backendMessage || 'Invitation created. No email was sent.',
                    inviteUrl: response.inviteUrl || '',
                    payload: {
                        userInviteId: response.userInviteId,
                        email: formData.email,
                        fullName: formData.fullName,
                        emailDelivery: 'disabled',
                    },
                });
                return;
            }
            if (backendMessage.includes('resent')) {
                setSuccessMessage('Invitation resent successfully! A new notification email has been sent. Please check your spam/junk folder if you do not receive it in your inbox.');
            } else {
                setSuccessMessage('Invitation sent successfully! A notification email has been sent. Please check your spam/junk folder if you do not receive it in your inbox.');
            }
            if (onSuccess) onSuccess({
                userInviteId: response?.userInviteId,
                email: formData.email,
                fullName: formData.fullName,
                emailDelivery: 'sent',
            });
            setTimeout(() => onClose(), 5000);
        } catch (err) {
            if (err.response?.status === 409) {
                setErrorMessage(err.response.data?.message || 'This user is already a member of the organization.');
            } else {
                setErrorMessage('Error: ' + (err.response?.data?.message || err.message));
            }
        } finally {
            setSending(false);
        }
    };

    const selectedComposite = formData.role && formData.scope
        ? `${formData.role}_${formData.scope}`
        : '';

    const handleDisabledClose = () => {
        const payload = disabledResult?.payload;
        setDisabledResult(null);
        if (payload && onSuccess) onSuccess(payload);
        onClose();
    };

    if (disabledResult) {
        return (
            <Modal isOpen={isOpen} onClose={handleDisabledClose} title="Add New User">
                <div className="space-y-4">
                    <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                        {disabledResult.message}
                    </div>
                    {disabledResult.inviteUrl && <InviteLinkBox url={disabledResult.inviteUrl} />}
                    <div className="flex justify-end">
                        <Button type="button" onClick={handleDisabledClose}>
                            Close
                        </Button>
                    </div>
                </div>
            </Modal>
        );
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Add New User">
            <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    onBlur={(e) => checkDomain(e.target.value)}
                    required
                />
                <Input
                    label="Full Name"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    required
                />
                <Select
                    label="Role"
                    value={selectedComposite}
                    onChange={handleRoleChange}
                    disabled={loadingRoles}
                    placeholder="-- Select Role --"
                    options={roleOptions.map(r => ({
                        value: `${r.role}_${r.scope}`,
                        label: r.displayName,
                    }))}
                />
                {successMessage && (
                    <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800">
                        {successMessage}
                    </div>
                )}
                {errorMessage && (
                    <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
                        {errorMessage}
                    </div>
                )}
                <div className="flex justify-end space-x-3">
                    <Button type="button" variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={sending || !!successMessage || loadingRoles}>
                        {sending ? 'Sending...' : 'Send Invite'}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default InviteUserModal;
